import React from 'react';
import cn from 'classnames';
import { FormikProvider, Form, useFormik } from 'formik';

import { paramsSchema, Params } from 'api/companies/public/schedule-meeting/params';
import { useTranslation, useAppDispatch, useResponsive, useAppSelector } from 'client/hooks';
import { companies as companiesApi } from 'client/redux/api/companies';
import { Button, Loader } from 'client/components/common';
import { Phone } from 'client/components/common/form';
import { ScheduleMeetingPopUpData, setPopup } from 'client/redux/ui';
import { selectLang } from 'client/redux/settings/selectors';
import { ENTITY_TYPES, TIME_SPOTS } from 'constants/index';

import { SuccessMessage } from './SuccessMessage';
import { ModalWrapper } from '../ModalWrapper';
import { Input } from './Input';
import { TimeSpots } from './TimeSpots';
import { SelectComponent as Select } from './Select';

import goForIsraelBigLogo from 'client/assets/goforisrael-big.svg';

import css from './SchedulePopup.module.scss';

interface Props {
  data: ScheduleMeetingPopUpData;
}

export const SchedulePopup: React.FC<Props> = ({ data }) => {
  const { companyId, companyName } = data;
  const [isMobile] = useResponsive(['MOBILE']);
  const { translate: _t } = useTranslation('popups');
  const dispatch = useAppDispatch();
  const lang = useAppSelector(selectLang);
  const [scheduleMeeting, scheduleMeetingState] = companiesApi.endpoints.scheduleMeeting.useMutation();

  const formikContext = useFormik<Params>({
    enableReinitialize: true,
    validationSchema: paramsSchema,
    initialValues: {
      companyId,
      message: '',
      phone: '',
      timeSpot: '',
      entityType: 0,
      fullName: '',
    },
    onSubmit: async (values) => {
      await scheduleMeeting(values).unwrap();
    },
  });

  const onClose = () => {
    dispatch(setPopup(null));
  };

  const renderContent = () => {
    return (
      <>
        {scheduleMeetingState.isSuccess ? (
          <SuccessMessage onClose={onClose} />
        ) : (
          <>
            <div className={css.goForIsraelBigLogo}>
              <img src={goForIsraelBigLogo} alt="go for Israel" />
            </div>
            <h6 className={css.title}>
              {companyName}
              <br />
              {_t('schedulePopup.title')}
            </h6>
            <p className={css.info}>{_t('schedulePopup.subDescription')}</p>
            <FormikProvider value={formikContext}>
              <Form className={cn(css.form, formikContext.isSubmitting && css.inactive)}>
                <Input name="fullName" type="input" label={_t('schedulePopup.fullNamePlaceholder')} />
                <Select
                  name={'entityType'}
                  placeholder={_t('schedulePopup.entityTypePlaceholder')}
                  options={ENTITY_TYPES}
                />
                <Phone name="phone" placeholder={_t('schedulePopup.phonePlaceholder')} locale={lang} />
                <TimeSpots name="timeSpot" options={TIME_SPOTS} label={_t('schedulePopup.timeSpotLabel')} />
                <Input name="message" type="textarea" label={_t('schedulePopup.messagePlaceholder')} />
                <div className={css.actions}>
                  {scheduleMeetingState.isLoading ? (
                    <Loader position="relative" className={css.loader} />
                  ) : (
                    <Button className={css.submitButton} type="submit">
                      {_t('schedulePopup.submitBtn')}
                    </Button>
                  )}
                </div>
              </Form>
            </FormikProvider>
          </>
        )}
      </>
    );
  };

  if (isMobile) {
    return <div className={css.popupContent}>{renderContent()}</div>;
  }

  return (
    <ModalWrapper className={css.modal} contentClassName={css.scheduleContent}>
      <div className={css.popupContent}>{renderContent()}</div>
    </ModalWrapper>
  );
};
